import React from 'react';
import { PropTypes } from 'prop-types';
import { Badge, Skeleton, Table } from '@mantine/core';
import { Link } from 'react-router-dom';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { currencyFormat } from '../../../helpers/format';
import {
  getPaymentPlanSummary,
  PROCESSING_COLOR
} from '../../../helpers/customerPaymentsHelper';

const CELL_STYLE = { padding: '10px 12px' };
const AMOUNT_CELL_STYLE = { padding: '10px 12px', textAlign: 'end' };

const PaymentPlanListItem = ({ plan }) => {
  const summary = plan ? getPaymentPlanSummary(plan) : null;

  return plan ? (
    <Table.Tr
      style={{
        backgroundColor: summary.needsCardUpdate
          ? 'rgba(196, 0, 0, 0.07)'
          : summary.isProcessing
          ? 'rgba(253, 126, 20, 0.07)'
          : undefined
      }}
    >
      <Table.Td style={CELL_STYLE}>
        <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
          {plan.logoImageUrl && (
            <img
              alt={plan.associationName ?? 'Association'}
              height={28}
              src={plan.logoImageUrl}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              width={28}
            />
          )}
          <AppStack style={{ gap: 0 }}>
            <AppText
              style={{ fontSize: 14, whiteSpace: 'nowrap' }}
              weight={500}
            >
              {plan.associationName ?? 'Payment plan'}
            </AppText>
            <AppText
              style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}
            >
              Order # {plan.pkRegCart}
            </AppText>
          </AppStack>
        </AppFlexbox>
      </Table.Td>

      <Table.Td style={AMOUNT_CELL_STYLE}>
        <AppText style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
          {currencyFormat(plan.amountInCents / 100)}
        </AppText>
      </Table.Td>

      <Table.Td style={AMOUNT_CELL_STYLE}>
        <AppText
          style={{
            fontSize: 14,
            whiteSpace: 'nowrap',
            color: plan.failedInCents > 0 ? '#C40000' : undefined
          }}
          weight={500}
        >
          {currencyFormat(summary.outstandingInCents / 100)}
        </AppText>
      </Table.Td>

      <Table.Td style={CELL_STYLE}>
        <AppStack style={{ gap: 0 }}>
          <AppText style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            {summary.nextDueLabel ?? '—'}
          </AppText>
          {summary.isProcessing && (
            <AppText
              color={PROCESSING_COLOR}
              style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              weight={500}
            >
              Processing
            </AppText>
          )}
        </AppStack>
      </Table.Td>

      <Table.Td style={CELL_STYLE}>
        <Badge color={summary.statusColor} variant="light">
          {summary.statusLabel}
        </Badge>
      </Table.Td>

      <Table.Td style={AMOUNT_CELL_STYLE}>
        <AppFlexbox
          style={{ gap: 12, justifyContent: 'flex-end', flexWrap: 'nowrap' }}
        >
          <AppText
            component={Link}
            style={{
              fontSize: 14,
              textDecoration: 'none',
              color: 'dodgerblue',
              whiteSpace: 'nowrap'
            }}
            to={`/payments/plans/${plan.pkRegCart}/${plan.pkRegCartPayment}`}
            weight={500}
          >
            Manage
          </AppText>
        </AppFlexbox>
      </Table.Td>
    </Table.Tr>
  ) : (
    <Table.Tr>
      <Table.Td style={CELL_STYLE}>
        <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
          <Skeleton height={28} width={28} />
          <AppStack style={{ gap: 5 }}>
            <Skeleton height={12} width={140} />
            <Skeleton height={10} width={80} />
          </AppStack>
        </AppFlexbox>
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={12} width={60} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={12} width={60} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={12} width={90} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={20} width={80} />
      </Table.Td>
      <Table.Td style={CELL_STYLE}>
        <Skeleton height={12} width={60} />
      </Table.Td>
    </Table.Tr>
  );
};

PaymentPlanListItem.propTypes = { plan: PropTypes.object };

export default PaymentPlanListItem;
